import { useEffect, useRef, useState } from 'react'
import { ExecutionAPI } from '../../../../../api'

const useHooks = ({ dataAll }) => {
  const debounceRef = useRef(null)
  const [tallySheet, setTallySheet] = useState([])
  const [materialTypes, setMaterialTypes] = useState([])
  const [uomQuantities, setUOMQuantities] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isFirstLoading, setIsFirstLoading] = useState(true)
  const [modalDelete, setModalDelete] = useState({
    isOpen: false,
    data: undefined
  })
  const [search, setSearch] = useState('')
  const [filteredTallySheet, setFilteredTallySheet] = useState([])

  const toggleModalDelete = () => {
    setModalDelete((prevState) => ({
      isOpen: !prevState.isOpen,
      data: undefined
    }))
  }

  const handleAddNew = () => {
    const newRow = {
      product_code: '',
      product_type: '',
      uom_quantity: '',
      quantity: 0,
      start_date: null,
      finish_date: null,
      vehicle_number: '',
      driver_name: '',
      remarks: '',
      disableStart: false,
      disableStop: true
    }
    setTallySheet((prev) => [...prev, newRow])
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setFilteredTallySheet(
        tallySheet.filter((row) =>
          row.product_name?.toLowerCase().includes(search.toLowerCase())
        )
      )
    }
  }

  const handleChange = (idx, field, value) => {
    setTallySheet((prev) => {
      const updated = prev.map((row, i) =>
        i === idx ? { ...row, [field]: value } : row
      )
      let qty = 0
      if (field === 'product_name') {
        const selectedOption = materialTypes.find(
          (opt) => opt.product_name === value
        )
        if (selectedOption) {
          updated[idx].product_type = selectedOption.product_type
          updated[idx].product_name = selectedOption.product_name
        }
      }
      return updated.map((row) => {
        qty += Number(row.quantity) || 0
        return { ...row, calculated_quantity: qty }
      })
    })

    const item = {
      ...tallySheet[idx],
      [field]: value
    }

    if (field === 'product_name' && !item.code) {
      const selectedOption = materialTypes.find(
        (opt) => opt.product_name === value
      )
      onSaveTallySheet({
        ...item,
        sr_code: dataAll.Code,
        product_type: selectedOption?.product_type,
        product_name: selectedOption?.product_name
      })
    }
    if (field === 'uom_quantity') {
      // Debounce update
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        onUpdateTallySheet({
          code: item.code,
          remarks: item.remarks,
          sr_code: dataAll.Code,
          end_date: item.end_date,
          quantity: parseFloat(item.quantity),
          start_date: item.start_date,
          driver_name: item.driver_name,
          product_name: item.product_name,
          vehicle_number: item.vehicle_number,
          product_code: item.product_code,
          product_type: item.product_type,
          uom_quantity: item.uom_quantity
        })
      }, 500) // 500ms debounce
    }
  }

  const handleUpdate = (idx) => {
    const item = {
      ...tallySheet[idx]
    }
    // Debounce update
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      onUpdateTallySheet({
        code: item.code,
        remarks: item.remarks,
        sr_code: dataAll.Code,
        end_date: item.end_date,
        quantity: parseFloat(item.quantity),
        start_date: item.start_date,
        driver_name: item.driver_name,
        product_name: item.product_name,
        vehicle_number: item.vehicle_number,
        product_code: item.product_code,
        product_type: item.product_type,
        uom_quantity: item.uom_quantity
      })
    }, 500) // 500ms debounce
  }

  const handleStart = (idx, value) => {
    if (value) return
    const item = {
      ...tallySheet[idx],
      start_date: new Date()
    }

    onUpdateTallySheet({
      code: item.code,
      remarks: item.remarks,
      sr_code: dataAll.Code,
      end_date: item.end_date,
      quantity: parseFloat(item.quantity),
      start_date: item.start_date,
      driver_name: item.driver_name,
      product_name: item.product_name,
      vehicle_number: item.vehicle_number,
      product_code: item.product_code,
      product_type: item.product_type,
      uom_quantity: item.uom_quantity
    })
  }

  const handleDelete = (idx) => {
    const item = tallySheet[idx]
    if (!item.code) {
      setTallySheet((prev) => prev.filter((_, i) => i !== idx))
      return
    }
    setModalDelete({
      isOpen: true,
      data: item
    })
  }

  const handleStop = (idx, value) => {
    if (!value) return
    const item = {
      ...tallySheet[idx],
      end_date: new Date()
    }

    onUpdateTallySheet({
      code: item.code,
      remarks: item.remarks,
      sr_code: dataAll.Code,
      end_date: item.end_date,
      quantity: parseFloat(item.quantity),
      start_date: item.start_date,
      driver_name: item.driver_name,
      product_name: item.product_name,
      vehicle_number: item.vehicle_number,
      product_code: item.product_code,
      product_type: item.product_type,
      uom_quantity: item.uom_quantity
    })
  }

  const parseDate = (dateStr) => {
    if (
      !dateStr ||
      dateStr === '0001-01-01T00:00:00Z' ||
      dateStr.includes('0000')
    ) {
      return null
    }
    return new Date(dateStr)
  }

  // isRefressh used when called after insert new data
  const onGetTallySheet = async (srCode, isRefresh) => {
    try {
      setIsLoading(true)
      if (isRefresh) {
        setIsFirstLoading(true)
      }
      const response = await ExecutionAPI.getTallySheet(srCode)
      if (response && response.data) {
        const calculatedQuantity = response.data.data?.reduce(
          (sum, row) => sum + (Number(row.quantity) || 0),
          0
        )
        const constructedTallySheetRaw =
          response.data.data?.map((item) => ({
            ...item,
            disableStart: !!parseDate(item.start_date),
            disableStop:
              !!parseDate(item.end_date) || !parseDate(item.start_date),
            calculated_quantity: calculatedQuantity,
            start_date: parseDate(item.start_date),
            end_date: parseDate(item.end_date),
            created_date: parseDate(item.created_date),
          })) || []

        const hasAnyStartDate = constructedTallySheetRaw.some(
          (row) => row.start_date
        )

        const constructedTallySheet = hasAnyStartDate
          ? constructedTallySheetRaw.sort((a, b) => {
              if (!a.start_date) return 1
              if (!b.start_date) return -1
              return new Date(a.start_date) - new Date(b.start_date)
            })
          : constructedTallySheetRaw
        setTallySheet(constructedTallySheet)
      }
    } catch (error) {
      console.error('Error fetching tally sheet:', error)
      setTallySheet([])
    } finally {
      setIsLoading(false)
      setIsFirstLoading(false)
    }
  }

  const onSaveTallySheet = async (params) => {
    try {
      setIsLoading(true)
      const response = await ExecutionAPI.insertTallySheet(params)
      if (response && response.data) {
        onGetTallySheet(params.sr_code, true)
      }
    } catch (error) {
      console.error('Error fetching tally sheet:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const onUpdateTallySheet = async (params) => {
    try {
      setIsLoading(true)
      const response = await ExecutionAPI.updateTallySheet(params)
      if (response && response.data) {
        onGetTallySheet(params.sr_code)
      }
    } catch (error) {
      console.error('Error fetching tally sheet:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const onDeleteTallySheet = async (params) => {
    try {
      setIsLoading(true)
      const response = await ExecutionAPI.deleteTallySheet(params.code)
      if (response && response.data) {
        onGetTallySheet(params.sr_code)
      }
    } catch (error) {
      console.error('Error deleting tally sheet:', error)
    } finally {
      setIsLoading(false)
      toggleModalDelete()
    }
  }

  const getMaterialType = async () => {
    try {
      const result = await ExecutionAPI.getCargoData(dataAll.Sr_No)
      const updatedResult = result.data.data ?? []
      const uomQuantities = (result.data.data ?? [])
        .filter((item) => item.uom_quantity)
        .map((item) => ({
          ...item,
          label: item.uom_quantity,
          value: item.uom_quantity
        }))
      setUOMQuantities(uomQuantities)
      setMaterialTypes(updatedResult)
    } catch (error) {
      return error
    }
  }

  useEffect(() => {
    const fetchAll = async () => {
      await getMaterialType()
      if (dataAll && dataAll.Code) {
        await onGetTallySheet(dataAll.Code)
      }
    }
    fetchAll()
  }, [dataAll?.sr_code])

  useEffect(() => {
    setFilteredTallySheet(tallySheet)
  }, [tallySheet])

  return {
    data: {
      search,
      setSearch,
      isLoading,
      tallySheet,
      modalDelete,
      materialTypes,
      uomQuantities,
      isFirstLoading,
      filteredTallySheet
    },
    methods: {
      handleStop,
      handleStart,
      handleChange,
      handleDelete,
      handleUpdate,
      handleAddNew,
      toggleModalDelete,
      onDeleteTallySheet,
      handleSearchKeyDown
    }
  }
}

export default useHooks
